import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import apiClient from "../../../api/apiClient";
import { useLoadScript, Autocomplete } from "@react-google-maps/api";
import MultiSelectDropdown from "../MultiSelectDropdown";

const GOOGLE_MAP_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const libraries = ["places"];

const daysOfWeek = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const defaultAvailability = daysOfWeek.reduce((acc, day) => {
  acc[day] = { closed: false, slots: [{ from: "", to: "" }] };
  return acc;
}, {});

export default function FormDoctor() {
  const navigate = useNavigate();
  const { id } = useParams();
  const autocompleteRef = useRef(null);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAP_KEY,
    libraries,
  });

  const [formData, setFormData] = useState({
    doctor_name: "",
    doctor_email: "", 
    phone_number: "",
    whatsapp_number: "",
    total_experience: "",
    consultation_fees: "",
    short_bio: "",
    address: "",
    city: "",
    state: "",
    country: "India",
    pincode: "",
  });

  const [specialityList, setSpecialityList] = useState([]);
  const [selectedSpecialities, setSelectedSpecialities] = useState([]); 

  const [availability, setAvailability] = useState(
    daysOfWeek.reduce((acc, day) => {
      acc[day] = { closed: false, slots: [{ from: "", to: "" }] };
      return acc;
    }, {})
  );

  const [doctorImage, setDoctorImage] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);

  const [errors, setErrors] = useState({});

  /* ------------------ LOAD SPECIALITIES ------------------ */
  useEffect(() => {
    let mounted = true;
    apiClient
      .get("/doctors/specialities-with-sub")
      .then((res) => {
        const rows = res?.data?.data || res?.data || [];
        const grouped = {};
        rows.forEach((row) => {
          if (!grouped[row.speciality_id]) {
            grouped[row.speciality_id] = {
              id: row.speciality_id,
              name: row.speciality_name,
              subs: [],
            };
          }
          if (row.sub_id) {
            grouped[row.speciality_id].subs.push({
              id: row.sub_id,
              name: row.sub_speciality_name,
            });
          }
        });
        if (mounted) setSpecialityList(Object.values(grouped));
      })
      .catch((err) => {
        console.error("Error loading specialities", err);
      });

    return () => (mounted = false);
  }, []);

  /* ------------------ LOAD DOCTOR FOR EDIT ------------------ */
 useEffect(() => {
  if (!id) return;

  apiClient.get(`/doctors/${id}`).then((res) => {
    const doctor = res.data.data;
    if (!doctor) return;

    setFormData({
      doctor_name: doctor.doctor_name || "",
      doctor_email: doctor.doctor_email || "",
      phone_number: doctor.phone_number || "",
      whatsapp_number: doctor.whatsapp_number || "",
      total_experience: doctor.total_experience || "",
      consultation_fees: doctor.consultation_fees || "",
      short_bio: doctor.bio || "",
      address: doctor.address || "",
      city: doctor.city || "",
      state: doctor.state || "",
      country: doctor.country || "India",
      pincode: doctor.pincode || "",
    });

    const ids = Array.isArray(doctor.speciality_id)
    ? doctor.speciality_id
    : JSON.parse(doctor.speciality_id || "[]");

    const selected = specialityList
      .flatMap(sp => [sp, ...(sp.subs || [])])
      .filter(item => ids.includes(item.id));

    setSelectedSpecialities(
      Array.isArray(doctor.speciality_id)
        ? doctor.speciality_id
        : JSON.parse(doctor.speciality_id || "[]")
    );


    setAvailability(
      doctor.availability
        ? JSON.parse(doctor.availability)
        : availability
    );

    if (doctor.doctor_images?.length > 0) {
      setPreviewImage(doctor.doctor_images[0].image_url);
    }
  });
}, [id]);


  /* ------------------ GENERIC HANDLERS ------------------ */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const toggleClosedDay = (day) => {
    setAvailability((prev) => ({
      ...prev,
      [day]: { ...prev[day], closed: !prev[day].closed },
    }));
  };

  const handleTimeChange = (day, index, field, value) => {
    setAvailability((prev) => {
      const updated = [...prev[day].slots];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, [day]: { ...prev[day], slots: updated } };
    });
  };

  const addSlot = (day) => {
    setAvailability((prev) => ({
      ...prev,
      [day]: { ...prev[day], slots: [...prev[day].slots, { from: "", to: "" }] },
    }));
  };

  const removeSlot = (day, index) => {
    setAvailability((prev) => {
      const updated = prev[day].slots.filter((_, i) => i !== index);
      return {
        ...prev,
        [day]: { ...prev[day], slots: updated.length ? updated : [{ from: "", to: "" }] },
      };
    });
  };

  /* ------------------ VALIDATION ------------------ */
  const validateForm = () => {
    const err = {};
    if (!formData.doctor_name || !formData.doctor_name.trim()) err.doctor_name = "Doctor name is required";
    if (!formData.doctor_email || !formData.doctor_email.includes("@")) err.doctor_email = "Valid email is required";
    if (!formData.phone_number || formData.phone_number.replace(/\D/g, "").length < 10) err.phone_number = "Valid phone is required";
    if (!formData.whatsapp_number || formData.whatsapp_number.replace(/\D/g, "").length < 10) err.whatsapp_number = "Valid whatsapp number is required";
    if (!formData.total_experience || !formData.total_experience.trim()) err.total_experience = "Total year of experience is required";
    if (!formData.consultation_fees || !formData.consultation_fees.trim()) err.consultation_fees = "consultation fees is required";
    if (!selectedSpecialities || selectedSpecialities.length === 0) err.specialities = "Select at least one speciality";
    if (!formData.address || !formData.address.trim()) err.address = "Address is required";
    if (!formData.short_bio || formData.short_bio.trim() === "") {
        err.short_bio = "Short bio is required";
    }

    // image is required only when creating
    if (!id && !doctorImage) err.image = "Doctor image is required";

    // validate availability times (if day is open)
    daysOfWeek.forEach((day) => {
      const dayObj = availability[day];
      if (dayObj && !dayObj.closed) {
        dayObj.slots.forEach((s, idx) => {
          if (!s.from || !s.to) {
            err[`availability_${day}_${idx}`] = "Both from & to required";
          } else {
            // simple time order check
            const from = s.from;
            const to = s.to;
            if (from >= to) {
              err[`availability_${day}_${idx}`] = "Opens time must be before close time";
            }
          }
        });
      }
    });

    setErrors(err);
    return Object.keys(err).length === 0;
  };

  /* ------------------ SUBMIT (CREATE / UPDATE) ------------------ */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix form errors");
      return;
    }

    const body = new FormData();

    // doctor_email, bio, speciality_ids (array JSON), availability JSON
    body.append("doctor_name", formData.doctor_name);
    body.append("doctor_email", formData.doctor_email);
    body.append("phone_number", formData.phone_number);
    body.append("whatsapp_number", formData.whatsapp_number);
    body.append("total_experience", formData.total_experience);
    body.append("consultation_fees", formData.consultation_fees);
    body.append("bio", formData.short_bio);
    body.append("address", formData.address);
    body.append("city", formData.city || "");
    body.append("state", formData.state || "");
    body.append("country", formData.country || "India");
    body.append("pincode", formData.pincode || "");
    // speciality_ids as JSON string
    body.append("speciality_ids",JSON.stringify(selectedSpecialities));
    console.log("Selected Specialities:", selectedSpecialities);

    // availability as JSON string
    body.append("availability", JSON.stringify(availability || {}));

    if (doctorImage) {
      body.append("doctor_image", doctorImage);
    }

    try {
      if (id) {
        await apiClient.post(`/doctors/update/${id}`, body, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Doctor updated successfully");
      } else {
        await apiClient.post("/doctors", body, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Doctor added successfully");
      }
      navigate("/admin/medical-tourism-doctors");
    } catch (err) {
      console.error("Submit error", err);
      toast.error("Something went wrong while saving doctor");
    }
  };

  /* ------------------ AUTOCOMPLETE (Google) ------------------ */
  const handlePlaceChanged = () => {
    if (!autocompleteRef.current) return;
    const place = autocompleteRef.current.getPlace();
    if (!place || !place.address_components) return;

    const comp = (type) => place.address_components.find((c) => c.types.includes(type))?.long_name || "";

    setFormData((p) => ({
      ...p,
      address: place.formatted_address,
      city: comp("locality") || comp("sublocality") || p.city,
      state: comp("administrative_area_level_1") || p.state,
      country: comp("country") || p.country,
      pincode: comp("postal_code") || p.pincode,
    }));
  };

  /* ------------------ RENDER ------------------ */
  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="accordion" id="doctorAccordion">

        {/* ------------------ BASIC DETAILS ------------------ */}
        <div className="accordion-item">
          <h2 className="accordion-header">
            <button className="accordion-button" type="button" data-bs-toggle="collapse" data-bs-target="#basic">
              Doctor Details
            </button>
          </h2>

          <div id="basic" className="accordion-collapse collapse show">
            <div className="accordion-body">
              <div className="row g-3">

                <div className="col-md-6">
                  <div className={`floating-field ${formData.doctor_name ? 'has-value' : ''}`}>
                    <input
                      className={`form-control ${errors.doctor_name ? "is-invalid" : ""}`}
                      name="doctor_name" 
                      placeholder="Enter doctor name"
                      value={formData.doctor_name}
                      onChange={handleChange}
                      readOnly={!!id}
                    />
                    <label className="floating-label">Name</label>
                  </div>
                  {errors.doctor_name && <div className="invalid-feedback d-block">{errors.doctor_name}</div>}
                </div>

                <div className="col-md-6">
                  <div className={`floating-field ${formData.doctor_email ? 'has-value' : ''}`}>
                    <input
                      className={`form-control ${errors.doctor_email ? "is-invalid" : ""}`}
                      placeholder="Enter email"
                      name="doctor_email"
                      value={formData.doctor_email}
                      onChange={handleChange}
                    />
                    <label className="floating-label">Email</label>
                  </div>
                  {errors.doctor_email && <div className="invalid-feedback d-block">{errors.doctor_email}</div>}
                </div>

                <div className="col-md-3">
                  <div className={`floating-field ${formData.phone_number ? 'has-value' : ''}`}>
                    <input
                      className={`form-control ${errors.phone_number ? "is-invalid" : ""}`}
                      placeholder="Enter phone"
                      name="phone_number"
                      value={formData.phone_number}
                      onChange={handleChange}
                    />
                    <label className="floating-label">Phone</label>
                  </div>
                  {errors.phone_number && <div className="invalid-feedback d-block">{errors.phone_number}</div>}
                </div>

                <div className="col-md-3">
                  <div className={`floating-field ${formData.whatsapp_number ? 'has-value' : ''}`}>
                    <input
                      className={`form-control ${errors.whatsapp_number ? "is-invalid" : ""}`}
                      placeholder="Enter WhatsApp"
                      name="whatsapp_number"
                      value={formData.whatsapp_number}
                      onChange={handleChange}
                    />
                    <label className="floating-label">WhatsApp</label>
                  </div>
                  {errors.whatsapp_number && <div className="invalid-feedback d-block">{errors.whatsapp_number}</div>}
                </div>

                <div className="col-md-3">
                  <div className={`floating-field ${formData.total_experience ? 'has-value' : ''}`}>
                    <input
                      type="number"
                      className={`form-control ${errors.total_experience ? "is-invalid" : ""}`}
                      placeholder="Years"
                      name="total_experience"
                      value={formData.total_experience}
                      onChange={handleChange}
                    />
                    <label className="floating-label">Experience (Years)</label>
                  </div>
                  {errors.total_experience && <div className="invalid-feedback d-block">{errors.total_experience}</div>}
                </div>

                <div className="col-md-3">
                  <div className={`floating-field ${formData.consultation_fees ? 'has-value' : ''}`}>
                    <input
                      type="number"
                      className={`form-control ${errors.consultation_fees ? "is-invalid" : ""}`}
                      placeholder="Enter fees"
                      name="consultation_fees"
                      value={formData.consultation_fees}
                      onChange={handleChange}
                    />
                    <label className="floating-label">Consultation Fees</label>
                  </div>
                  {errors.consultation_fees && <div className="invalid-feedback d-block">{errors.consultation_fees}</div>}
                </div>

                <div className="col-md-12">
                  <label className="form-label">Specialities</label>
                  <div className={errors.specialities ? "is-invalid-select" : ""}>
                    <MultiSelectDropdown
                      options={specialityList}
                      selectedValues={selectedSpecialities}
                      onChange={setSelectedSpecialities}
                    />
                  </div>
                  {errors.specialities && <div className="invalid-feedback d-block">{errors.specialities}</div>}
                </div>

                {/* GOOGLE ADDRESS */}
                <div className="col-md-12">
                  <label className="form-label">Address</label>

                  {isLoaded && (
                    <Autocomplete
                      onLoad={(ref) => (autocompleteRef.current = ref)}
                      onPlaceChanged={handlePlaceChanged}
                    >
                      <input
                        type="text"
                        className={`form-control ${errors.address ? "is-invalid" : ""}`}
                        placeholder="Search address"
                        value={formData.address}
                        onChange={(e) =>
                          setFormData((p) => ({ ...p, address: e.target.value }))
                        }
                      />
                    </Autocomplete>
                  )}

                  {errors.address && (
                    <div className="invalid-feedback d-block">{errors.address}</div>
                  )}
                </div>


                <div className="col-md-3">
                  <div className={`floating-field ${formData.city ? 'has-value' : ''}`}>
                    <input className="form-control" value={formData.city} readOnly placeholder="City" />
                    <label className="floating-label">City</label>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className={`floating-field ${formData.state ? 'has-value' : ''}`}>
                    <input className="form-control" value={formData.state} readOnly placeholder="State" />
                    <label className="floating-label">State</label>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className={`floating-field ${formData.country ? 'has-value' : ''}`}>
                    <input className="form-control" value={formData.country} readOnly placeholder="Country" />
                    <label className="floating-label">Country</label>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className={`floating-field ${formData.pincode ? 'has-value' : ''}`}>
                    <input className="form-control" value={formData.pincode} readOnly placeholder="Pincode" />
                    <label className="floating-label">Pincode</label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ------------------ AVAILABILITY ------------------ */}
        <div className="accordion-item">
          <h2 className="accordion-header">
            <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#avail">
              Doctor Availability
            </button>
          </h2>

          <div id="avail" className="accordion-collapse collapse">
            <div className="accordion-body">

              {daysOfWeek.map((day) => (
                <div key={day} className="mb-4 pb-3 border-bottom">
                  <div className="d-flex align-items-center">
                    <input
                      type="checkbox"
                      className="form-check-input me-2"
                      checked={availability[day]?.closed}
                      onChange={() => toggleClosedDay(day)}
                    />
                    <div>
                      <strong>{day}</strong>
                      <div className="text-muted small">Closed</div>
                    </div>
                  </div>

                  {!availability[day]?.closed && (
                    <div className="mt-2">
                      {(availability[day].slots || []).map((slot, index) => (
                        <div key={index} className="row g-3 mb-2" style={{ paddingLeft: 25 }}>
                          <div className="col-md-3">
                            <label className="text-muted small">Opens at</label>
                            <input
                              type="time"
                              className={`form-control ${errors[`availability_${day}_${index}`] ? "is-invalid" : ""}`}
                              placeholder="--:--"
                              value={slot.from}
                              onChange={(e) => handleTimeChange(day, index, "from", e.target.value)}
                            />
                            {errors[`availability_${day}_${index}`] && (
                              <div className="invalid-feedback">{errors[`availability_${day}_${index}`]}</div>
                            )}
                          </div>

                          <div className="col-md-3">
                            <label className="text-muted small">Closes at</label>
                            <input
                              type="time"
                              className={`form-control ${errors[`availability_${day}_${index}`] ? "is-invalid" : ""}`}
                              placeholder="--:--"
                              value={slot.to}
                              onChange={(e) => handleTimeChange(day, index, "to", e.target.value)}
                            />
                          </div>

                          <div className="col-md-2 d-flex align-items-end">
                            {index > 0 && (
                              <button
                                type="button"
                                className="btn btn-outline-danger me-2"
                                onClick={() => removeSlot(day, index)}
                              >
                                ✕
                              </button>
                            )}

                            {index === availability[day].slots.length - 1 && (
                              <button
                                type="button"
                                className="btn btn-outline-primary"
                                onClick={() => addSlot(day)}
                              >
                                +
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}

            </div>
          </div>
        </div>

        {/* ------------------ PROFILE ------------------ */}
        <div className="accordion-item">
          <h2 className="accordion-header">
            <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#profile">
              Profile & Description
            </button>
          </h2>
          <div id="profile" className="accordion-collapse collapse">
            <div className="accordion-body">
              <div className={`floating-field ${formData.short_bio ? 'has-value' : ''}`}>
                <textarea
                  className="form-control"
                  rows="4"
                  placeholder="Short bio / description"
                  name="short_bio"
                  value={formData.short_bio}
                  onChange={handleChange}
                />
                <label className="floating-label">Short bio / description</label>
              </div>
            </div>
          </div>
        </div>

        {/* ------------------ IMAGE UPLOAD (own accordion) ------------------ */}
        <div className="accordion-item">
          <h2 className="accordion-header">
            <button
              className="accordion-button collapsed"
              type="button"
              data-bs-toggle="collapse"
              data-bs-target="#collapseImage"
            >
              Doctor Image
            </button>
          </h2>

          <div id="collapseImage" className="accordion-collapse collapse">
            <div className="accordion-body">
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">Upload Doctor Image</label>
                  <input
                    type="file"
                    accept="image/*"
                    className={`form-control ${errors.image ? "is-invalid" : ""}`}
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setDoctorImage(file);
                        setPreviewImage(URL.createObjectURL(file));
                        setErrors((prev) => ({ ...prev, image: undefined }));
                      }
                    }}
                  />
                  {errors.image && <div className="invalid-feedback d-block">{errors.image}</div>}
                </div>

                <div className="col-md-6 d-flex align-items-center">
                  {previewImage ? (
                    <img
                      src={previewImage}
                      alt="Doctor Preview"
                      style={{
                        width: 140,
                        height: 140,
                        objectFit: "cover",
                        borderRadius: 10,
                        border: "1px solid #ddd",
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: 140,
                        height: 140,
                        background: "#f0f0f0",
                        borderRadius: 10,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "1px dashed #bbb",
                        color: "#666",
                      }}
                    >
                      No Image
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>

      <div className="text-end mt-3">
        <button type="submit" className="btn btn-primary">
          {id ? "Update Doctor" : "Save Doctor"}
        </button>
      </div>

      {/* Minimal inline styles for invalid select (multi-select wrapper) */}
      <style>{`
        .is-invalid {
          border-color: #e74c3c !important;
          background-image: none !important;
        }
        .is-invalid-select {
          border: 1px solid #e74c3c;
          padding: 6px;
          border-radius: 6px;
        }
        .invalid-feedback { color: #e74c3c; font-size: 13px; margin-top: 6px; }

        .field-error-tooltip {
          position: relative;
        }

        .field-error-tooltip::after {
          content: attr(data-error);
          position: absolute;
          top: 100%;
          left: 0;
          background: #e74c3c;
          color: white;
          font-size: 12px;
          padding: 4px 8px;
          border-radius: 4px;
          white-space: nowrap;
          margin-top: 4px;
          display: none;
          z-index: 10;
        }

        .field-error-tooltip.show-error::after {
          display: block;
        }

      `}</style>
    </form>
  );
}
